import { CircularProgress, useMediaQuery, useTheme } from "@mui/material";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import React, { useEffect, useState, useCallback, useRef } from "react";

// @asseinfo/react-kanban components

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

// @mui material components

// Add import for Tooltip

// Argon Dashboard 2 PRO MUI components
import { UseHandleKanban } from "0.Mine/1.Hooks/CRM/Kanban";
import { UseHandleLeads } from "0.Mine/1.Hooks/CRM/Leads";
import { useHandlePermissions } from "0.Mine/1.Hooks/security/permissions";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 PRO MUI example components
import { useArgonController } from "context";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

// Argon Dashboard 2 PRO MUI contexts

// Data
import BusinessNavbar from "examples/Navbars/DashboardNavbar/businessNavbar";
import showConfirmationAlert from "layouts/pages/sweet-alerts/components/WithSuccessAttachedFunction/CancelSomething";

import Card from "./components/Card";
import CreateBoards, { addCardsToBoard } from "./data";

// First, add a custom CSS style to adjust the dragging behavior

import StyledBoard from "./StyledBoard";

const Kanban = () => {
  return (
    <DashboardLayout disableAnimation={true}>
      <KanbanWithContext />
    </DashboardLayout>
  );
};

function KanbanWithContext() {
  const { t } = useTranslation();
  const [controller] = useArgonController();
  const { darkMode } = controller;
  const [newCardForm, setNewCardForm] = useState(false);
  const [formValue, setFormValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toggleDeleteMode, setToggleDeleteMode] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const theme = useTheme();
  const [vendor, setVendor] = useState([]);
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  // Track modified cards
  const [cards, setCards] = useState([]);
  const [board, setBoard] = useState(null);
  const cardsChangeTracker = useRef({
    added: [],
    moved: [],
    deleted: [],
  });

  const {
    createKanbanCard,
    SaveKanbanChanges,
    createKanbanBoard,
    getKanban,
    reorderKanban,
    deleteKanban,
    moveKanbanCard,
  } = UseHandleKanban();
  const { session } = useBusinessContext();
  const { getLeadStatusOptions } = UseHandleLeads();
  const { getVendors } = useHandlePermissions();
  const navigate = useNavigate();

  const openNewCardForm = useCallback((event, id) => setNewCardForm(id), []);
  const closeNewCardForm = useCallback(() => setNewCardForm(false), []);
  const handleSetFormValue = useCallback(
    ({ currentTarget }) => setFormValue(currentTarget.value),
    []
  );

  const extractAllCards = useCallback(() => {
    if (!board || !board.columns) return [];

    const allCards = [];
    board.columns.forEach((column) => {
      column.cards.forEach((card) => {
        // Create serializable version of the card
        const serializedCard = {
          id: card.id,
          content:
            typeof card.template === "string" ? card.template : card.content || "Component Card",
          columnId: column.id,
          badge: card.badge || { color: "info", label: "Task" },
          progress: card.progress || 0,
          attachedFiles: card.attachedFiles || "",
          members: card.members || [],
        };

        // Don't include React components
        if (React.isValidElement(card.template)) {
          delete serializedCard.template;
        }

        allCards.push(serializedCard);
      });
    });

    return allCards;
  }, [board]);

  // Handle card movement between columns
  const handleCardMove = useCallback(
    async (updatedBoard, card, source, destination) => {
      console.log("Card moved:", { card, source, destination });
      setBoard(updatedBoard);

      // Track the moved card
      if (source.fromColumnId !== destination.toColumnId) {
        cardsChangeTracker.current.moved.push({
          id: card.id,
          fromColumnId: source.fromColumnId,
          toColumnId: destination.toColumnId,
        });
      }

      const updatedMovement = await moveKanbanCard(
        session.selectedBusiness.id,
        card.id,
        source.fromColumnId,
        destination.toColumnId
      );
      console.log(updatedMovement);
      // Update cards array to stay in sync with board
      setCards(extractAllCards());

      // Log the new positions of all columns
      setTimeout(() => {
        const columnPositions = {};
        updatedBoard.columns.forEach((column, index) => {
          columnPositions[column.title] = index + 1;
        });

        const positionLog = Object.entries(columnPositions)
          .map(([name, position]) => `${name}:${position}`)
          .join(", ");

        console.log(`Column positions after move: [${positionLog}]`);
      }, 0);
    },
    [extractAllCards]
  );

  // Fix the createNewCard function
  const createNewCard = useCallback(
    async (columnId) => {
      if (!formValue.trim()) return;

      // Find the actual column from your board to get consistent IDs
      const column = board.columns.find((col) => col.id === columnId);
      if (!column) {
        console.error(`Column with ID ${columnId} not found`);
        return;
      }

      console.log("Creating card in column:", column.title, "with ID:", columnId);

      // Create the card with consistent structure
      const newCardData = {
        id: uuidv4(),
        content: formValue,
        badge: { color: "info", label: "New Task" },
        progress: 0,
        attachedFiles: "",
        members: [],
        columnId: column.title, // Use the provided columnId
        position: column.title, // Add position using the column title for backend
      };

      // Create the card with React component
      const newCard = {
        ...newCardData,
        template: (
          <Card
            content={newCardData.content}
            badge={newCardData.badge}
            progress={newCardData.progress}
            attachedFiles={newCardData.attachedFiles}
            members={newCardData.members}
          />
        ),
      };

      // Track the added card
      cardsChangeTracker.current.added.push(newCardData);

      try {
        // Send to backend with position information
        const id = await createKanbanCard(session.selectedBusiness.id, {
          ...newCardData,
          priorityBadge: newCardData.badge.label.toLowerCase(),
          title: newCardData.content,
        });

        //update the newCard with the ID from the backend
        newCardData.id = id;

        console.log("Card created successfully:", newCardData);
      } catch (error) {
        console.error("Error saving card to backend:", error);
      }

      // Reset form and close it
      setFormValue("");
      closeNewCardForm();

      // Update both local states
      setCards((prev) => [...prev, newCardData]);

      // Update the board state (this is the key fix)
      const updatedBoard = { ...board };
      const columnIndex = updatedBoard.columns.findIndex((col) => col.id === columnId);
      if (columnIndex !== -1) {
        // Add the new card to the column's cards array
        updatedBoard.columns[columnIndex].cards.push(newCard);
        setBoard(updatedBoard);
      }
    },
    [formValue, closeNewCardForm, session, board]
  );

  //Handles deleting a card
  const handleDeleteCard = useCallback(
    (cardId, columnId) => {
      if (!board) return;

      const updatedBoard = { ...board };
      const columnIndex = updatedBoard.columns.findIndex((col) => col.id === columnId);

      if (columnIndex !== -1) {
        const cardIndex = updatedBoard.columns[columnIndex].cards.findIndex(
          (card) => card.id === cardId
        );

        if (cardIndex !== -1) {
          // Remove the card
          updatedBoard.columns[columnIndex].cards.splice(cardIndex, 1);
          setBoard(updatedBoard);

          // Track the deleted card
          cardsChangeTracker.current.deleted.push({ id: cardId, columnId });

          // Update cards array
          setCards(extractAllCards());
        }
      }
    },
    [board, extractAllCards]
  );

  useEffect(() => {
    return () => {
      try {
        // Get the current state directly
        const allCurrentCards = extractAllCards();

        console.log("Final Kanban board state on unmount:", {
          cards: allCurrentCards,
          changes: {
            added: cardsChangeTracker.current.added,
            moved: cardsChangeTracker.current.moved,
            deleted: cardsChangeTracker.current.deleted,
          },
        });

        // If we have cards and a business ID, save changes
        if (session?.selectedBusiness?.id && allCurrentCards.length > 0) {
          const changes = {
            businessId: session.selectedBusiness.id,
            added: cardsChangeTracker.current.added,
            moved: cardsChangeTracker.current.moved,
            deleted: cardsChangeTracker.current.deleted,
            allCards: allCurrentCards,
          };

          console.log("Saving changes:", changes);

          // Since we can't use await in the cleanup function directly,
          // we need to call the async function and handle the promise differently
          SaveKanbanChanges(session.selectedBusiness.id, changes)
            .then(() => console.log("Successfully saved kanban changes"))
            .catch((err) => console.error("Failed to save kanban changes:", err));
        }
      } catch (error) {
        console.error("Error in cleanup function:", error);
      }
    };
  }, [session]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    const fetchData = async () => {
      try {
        // Create default board structure
        const defaultBoard = CreateBoards([]);

        if (session?.selectedBusiness?.id) {
          try {
            // Fetch the kanban data
            const data = await getKanban(session.selectedBusiness.id);
            const boardWithColumns = CreateBoards(data.columns);
            setBoard(boardWithColumns);

            const statusOptions = await getLeadStatusOptions(session.selectedBusiness.id);
            setStatusOptions(statusOptions);
            const vendors = await getVendors(session.selectedBusiness.id);
            setVendor(vendors);
            console.log("VENDORS: ", vendors);
            console.log("STATUS OPTIONS V2 ARE: ", statusOptions);
            let fetchedCards = [];
            if (data.columns && Array.isArray(data.columns)) {
              data.columns.forEach((column) => {
                if (column.tasks && Array.isArray(column.tasks)) {
                  fetchedCards = [...fetchedCards, ...column.tasks];
                }
              });
            }

            console.log("FETCHED CARDS: ", fetchedCards);

            if (isMounted) {
              const validCards = Array.isArray(fetchedCards) ? fetchedCards : [];
              addCardsToBoard(boardWithColumns, validCards);
              setCards(validCards);
              setBoard(boardWithColumns);

              // Reset change tracker since we just loaded fresh data
              cardsChangeTracker.current = { added: [], moved: [], deleted: [] };
            }
          } catch (e) {
            console.error("Error fetching kanban cards:", e);
            if (isMounted) {
              setCards([]);
              setBoard(defaultBoard);
              setError("Failed to load cards. Using empty board.");
            }
          }
        } else {
          if (isMounted) {
            setCards([]);
            setBoard(defaultBoard);
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [session]);

  useEffect(() => {
    // This effect runs after board state changes
    if (board && board.columns) {
      // First, let's check if we have multiple "Lead da chiamare" boards
      const leadBoards = board.columns.filter((column) => column.title === "Lead da chiamare");

      // If we have duplicates, let's remove all but the first one
      if (leadBoards.length > 1) {
        console.log(`Found ${leadBoards.length} "Lead da chiamare" boards, removing duplicates`);
        const updatedBoard = { ...board };
        const firstLeadBoardId = leadBoards[0].id;

        // Keep only the first one and remove others
        updatedBoard.columns = updatedBoard.columns.filter(
          (column) => column.title !== "Lead da chiamare" || column.id === firstLeadBoardId
        );

        setBoard(updatedBoard);
        return; // Exit early after fixing duplicates
      }

      // Now proceed with the normal logic
      const leadDaChiamareIndex = board.columns.findIndex(
        (column) => column.title === "Lead da chiamare"
      );

      // If it doesn't exist, create it and place it first
      if (leadDaChiamareIndex === -1) {
        console.log("Creating required 'Lead da chiamare' board");

        const newColumnId = `column-${uuidv4()}`;
        const updatedBoard = { ...board };

        // Create the special column and place it at position 0
        updatedBoard.columns.unshift({
          id: newColumnId,
          title: "Lead da chiamare",
          cards: [],
          isLocked: true, // Mark this column as locked (cannot be deleted or moved)
        });

        setBoard(updatedBoard);

        // Create the column in the backend as well
        if (session?.selectedBusiness?.id) {
          createKanbanBoard(session.selectedBusiness.id, "Lead da chiamare")
            .then((columnId) => {
              // Update the column ID with the one from the backend
              const updatedBoardWithId = { ...updatedBoard };
              if (updatedBoardWithId.columns[0].title === "Lead da chiamare") {
                updatedBoardWithId.columns[0].id = columnId;
                setBoard(updatedBoardWithId);
              }
            })
            .catch((err) => console.error("Failed to create 'Lead da chiamare' board:", err));
        }
      }
      // If it exists but isn't first, move it to the first position
      else if (leadDaChiamareIndex !== 0) {
        console.log("Moving 'Lead da chiamare' board to first position");

        const updatedBoard = { ...board };
        const leadColumn = updatedBoard.columns.splice(leadDaChiamareIndex, 1)[0];
        updatedBoard.columns.unshift(leadColumn);

        setBoard(updatedBoard);

        // Update column positions in the backend
        if (session?.selectedBusiness?.id) {
          const columnPositions = updatedBoard.columns.reduce((acc, column, index) => {
            acc[column.id] = index + 1;
            return acc;
          }, {});

          reorderKanban(session.selectedBusiness.id, columnPositions).catch((err) =>
            console.error("Failed to reorder kanban with 'Lead da chiamare' first:", err)
          );
        }
      }
    }
  }, []);

  // Add this function to handle column deletion
  const handleDeleteColumn = useCallback(
    async (columnId) => {
      if (!board || !toggleDeleteMode) return;
      try {
        const deleteKanbanFromTable = async () => {
          const updatedBoard = { ...board };

          // Find the column to delete
          const columnIndex = updatedBoard.columns.findIndex((col) => col.id === columnId);

          if (columnIndex !== -1) {
            // Before removing, add all cards in this column to the deleted cards tracker
            const cardsInColumn = updatedBoard.columns[columnIndex].cards || [];
            cardsInColumn.forEach((card) => {
              cardsChangeTracker.current.deleted.push({
                id: card.id,
                columnId: columnId,
              });
            });

            // Remove the column
            updatedBoard.columns.splice(columnIndex, 1);
            setBoard(updatedBoard);

            // Update cards array
            setCards(extractAllCards());

            console.log(`Column ${columnId} deleted with ${cardsInColumn.length} cards`);
          }

          await deleteKanban(session.selectedBusiness.id, columnId);
        };
        const confirmed = await showConfirmationAlert({
          title: "Eliminare la Dashboard?",
          text: "Sei sicuro di voler eliminare la Dashboard? Questo eliminerà anche le schede associate.",
          icon: "warning",
          confirmButtonText: "Sì, elimina",
          cancelButtonText: "No, mantieni",
          successTitle: "Dashboard eliminata",
          successText: "La dashboard è stata eliminata con successo.",
          onConfirm: async () => {
            // Perform the actual cancellation
            await deleteKanbanFromTable();
          },
        });
      } catch (error) {
        console.error("Error in cancel booking process:", error);
      }
    },
    [board, toggleDeleteMode, extractAllCards]
  );

  const renderColumnHeader = useCallback(
    ({ id, title, isLocked }, { addCard }) => (
      <>
        <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <ArgonBox
            display="flex"
            alignItems="center"
            onClick={
              toggleDeleteMode && !isLocked && title !== "Lead da chiamare"
                ? () => handleDeleteColumn(id)
                : undefined
            }
            sx={{
              cursor:
                toggleDeleteMode && !isLocked && title !== "Lead da chiamare"
                  ? "pointer"
                  : "default",
              ...(toggleDeleteMode &&
                !isLocked &&
                title !== "Lead da chiamare" && {
                  "&:hover": {
                    color: "error.main",
                  },
                }),
            }}
          >
            {title === "Lead da chiamare" || isLocked ? (
              <Tooltip title="This column cannot be deleted or moved">
                <Icon sx={{ fontSize: 20, mr: 1, color: "info.main" }}>lock</Icon>
              </Tooltip>
            ) : !toggleDeleteMode ? (
              <Icon sx={{ fontSize: 20, mr: 1 }}>drag_indicator</Icon>
            ) : (
              <Tooltip title="Click to delete this column">
                <Icon sx={{ fontSize: 20, mr: 1, color: "error.light" }}>delete_sweep</Icon>
              </Tooltip>
            )}
            <ArgonTypography
              variant="h6"
              fontWeight="bold"
              textTransform="capitalize"
            ></ArgonTypography>
          </ArgonBox>
          <ArgonTypography variant="h6">{title}</ArgonTypography>
          <ArgonButton
            size="medium"
            variant={darkMode ? "outlined" : "contained"}
            onClick={(event) => openNewCardForm(event, id)}
            disabled={toggleDeleteMode} // Disable adding cards in delete mode
            sx={{
              minWidth: 36,
              height: 36,
              borderRadius: "999px",
              backgroundImage:
                !darkMode && 'url("https://qlickup.it/wp-content/uploads/2025/04/SFONDO-1.jpg")',
              backgroundSize: "cover!important",
              padding: "6px",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              minHeight: "unset",
              "&:hover": {
                rotate: "90deg",
                backgroundImage:
                  !darkMode && 'url("https://qlickup.it/wp-content/uploads/2025/04/SFONDO-1.jpg")',
              },
            }}
          >
            <Icon
              sx={{
                fontWeight: "bold",
                color: darkMode ? "#fff" : "#fff",
              }}
            >
              add
            </Icon>
          </ArgonButton>
        </ArgonBox>
        {newCardForm === id ? (
          <ArgonBox my={2.5}>
            <ArgonInput
              value={formValue}
              inputProps={{ rows: 2 }}
              onChange={handleSetFormValue}
              multiline
              placeholder={t("kanban.newCardLabel")}
            />
            <ArgonBox display="flex" mt={2}>
              <ArgonButton
                variant="gradient"
                color="primary"
                size="small"
                onClick={() => createNewCard(id)}
              >
                {t("kanban.add")}
              </ArgonButton>
              <ArgonBox ml={1}>
                <ArgonButton
                  variant="gradient"
                  color="light"
                  size="small"
                  onClick={closeNewCardForm}
                >
                  {t("kanban.cancel")}
                </ArgonButton>
              </ArgonBox>
            </ArgonBox>
          </ArgonBox>
        ) : null}
      </>
    ),
    [
      newCardForm,
      formValue,
      createNewCard,
      handleSetFormValue,
      openNewCardForm,
      closeNewCardForm,
      darkMode,
      toggleDeleteMode,
      handleDeleteColumn,
    ]
  );
  // Card renderer - used in both render paths
  const renderCard = useCallback(
    (card, { dragging }) => (
      <ArgonBox
        key={card.id}
        dragging={dragging.toString() || undefined}
        display="block"
        width={isTablet ? "calc(100% - 16px)" : "calc(450px - 40px)"}
        mt={2.5}
        sx={{
          cursor: toggleDeleteMode ? "pointer" : dragging ? "grabbing" : "grab",
          transform: dragging ? "rotate(3deg) scale(1.02)" : "none",
          transition: "all 0.2s ease",
          zIndex: dragging ? 1000 : 1,
          userSelect: "none",
          ...(toggleDeleteMode && {
            "&:hover": {
              backgroundColor: "rgba(244, 67, 54, 0.08)",
              border: "1px solid #f44336",
            },
          }),
        }}
        onClick={toggleDeleteMode ? () => handleDeleteCard(card.id, card.columnId) : undefined}
      >
        {React.isValidElement(card.template) ? (
          React.cloneElement(card.template, { statusOptions, vendors: vendor, id: card.id })
        ) : (
          <Card
            //badge={card.badge || { color: "info", label: "Task" }}
            content={typeof card.template === "string" ? card.template : card.content}
            progress={card.progress || 0}
            attachedFiles={card.attachedFiles || ""}
            members={card.members || []}
            onDelete={toggleDeleteMode ? undefined : () => handleDeleteCard(card.id, card.columnId)}
            statusOptions={statusOptions}
            vendors={vendor}
            id={card.id}
          />
        )}
      </ArgonBox>
    ),
    [isTablet, handleDeleteCard, toggleDeleteMode, statusOptions, vendor]
  );

  const [newColumnForm, setNewColumnForm] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState("");
  // Add handler for new column form
  const openNewColumnForm = useCallback(() => setNewColumnForm(true), []);
  const closeNewColumnForm = useCallback(() => {
    setNewColumnForm(false);
    setNewColumnTitle("");
  }, []);

  const handleNewColumnTitleChange = useCallback(
    ({ currentTarget }) => setNewColumnTitle(currentTarget.value),
    []
  );

  // Add function to create a new column
  const createNewColumn = useCallback(async () => {
    if (!newColumnTitle.trim() || !board) return;

    if (!session || !session.selectedBusiness || !session.selectedBusiness.id) {
      console.error("No valid session or business ID found");
      return;
    }
    // First create the column on the backend to get a real ID
    const columnId = await createKanbanBoard(session.selectedBusiness.id, newColumnTitle);

    if (!columnId) {
      console.error("Failed to create new column in the backend");
      return;
    }

    // Now create the column with the real backend ID
    const updatedBoard = { ...board };
    updatedBoard.columns.push({
      id: columnId, // Use the real ID from the backend directly
      title: newColumnTitle,
      cards: [],
    });

    setBoard(updatedBoard);
    closeNewColumnForm();

    // Log column positions after adding new column
    const columnPositions = {};
    updatedBoard.columns.forEach((column, index) => {
      columnPositions[column.title] = index + 1;
    });

    console.log(`New column created with ID: ${columnId}`);
  }, [board, newColumnTitle, session]);

  const handleRemoveColumn = useCallback(() => {
    setToggleDeleteMode((prevMode) => {
      const newMode = !prevMode;

      // Provide user feedback about the mode
      if (newMode) {
        console.log("Delete mode activated - click on cards to delete them");
        // You could also add a toast notification here
      } else {
        console.log("Delete mode deactivated");
      }

      return newMode;
    });
  }, []);

  const reorderKanbanBoards = useCallback(
    async (newBoard) => {
      // Ensure "Lead da chiamare" is always first
      const leadIndex = newBoard.columns.findIndex(
        (col) => col.title === "Lead da chiamare" || col.isLocked
      );

      if (leadIndex > 0) {
        // If it's not first, move it to the beginning
        const leadColumn = newBoard.columns.splice(leadIndex, 1)[0];
        newBoard.columns.unshift(leadColumn);
      }

      setBoard(newBoard);
      const columnPositions = newBoard.columns.reduce((acc, column, index) => {
        acc[column.id] = index + 1;
        return acc;
      }, {});

      await reorderKanban(session.selectedBusiness.id, columnPositions);
    },
    [session]
  );

  if (loading) {
    return (
      <div>
        <BusinessNavbar />
        <ArgonBox py={3} display="flex" justifyContent="center" alignItems="center" height="60vh">
          <ArgonTypography variant="h5" color="text" fontWeight="regular" mr={2}>
            Loading Kanban board...
          </ArgonTypography>
          <CircularProgress size={24} />
        </ArgonBox>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <BusinessNavbar />
      <ArgonBox py={3} px={isTablet ? 1 : 3}>
        {/* New Toolbar for better UX */}
        <ArgonBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          sx={{
            borderRadius: 1,
            p: 2,
            bgcolor: darkMode ? "background.dark" : "white",
            boxShadow: "0 0 8px rgba(0,0,0,0)",
            position: "relative",
          }}
        >
          <ArgonTypography variant="h5" fontWeight="medium">
            Gestione Leads
          </ArgonTypography>

          <ArgonBox display="flex" alignItems="center" position="relative">
            {" "}
            {/* Add position relative */}
            <Tooltip
              title={
                toggleDeleteMode
                  ? "Esci dalla Modalità di cancellazione"
                  : "Modalità di cancellazione"
              }
            >
              <ArgonButton
                variant="gradient"
                color={toggleDeleteMode ? "error" : "light"}
                size="small"
                onClick={handleRemoveColumn}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  transition: "all 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                  mr: 2,
                  // Add pulsing animation when active
                  ...(toggleDeleteMode && {
                    animation: "pulse 1.5s infinite",
                    "@keyframes pulse": {
                      "0%": {
                        boxShadow: "0 0 0 0 rgba(244, 67, 54, 0.4)",
                      },
                      "70%": {
                        boxShadow: "0 0 0 8px rgba(244, 67, 54, 0)",
                      },
                      "100%": {
                        boxShadow: "0 0 0 0 rgba(244, 67, 54, 0)",
                      },
                    },
                  }),
                }}
              >
                <Icon sx={{ fontSize: 16, mr: 0.5 }}>{toggleDeleteMode ? "cancel" : "delete"}</Icon>
                {toggleDeleteMode ? "Esci dalla Delete Mode" : "Delete Mode"}
              </ArgonButton>
            </Tooltip>
            <Tooltip title="Aggiungi nuova colonna">
              <ArgonButton
                variant="gradient"
                color="info"
                size="small"
                onClick={openNewColumnForm}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                  mr: 2, // Added margin right here
                }}
              >
                <Icon sx={{ fontSize: 16, mr: 0.5 }}>add</Icon>
                Aggiungi Colonna
              </ArgonButton>
            </Tooltip>
            <Tooltip title="Aggiungi lead in blocco">
              <ArgonButton
                variant="gradient"
                color="info"
                size="small"
                onClick={() => navigate("/crm/addBulkLeads", { replace: true })}
                sx={{
                  display: "flex",
                  alignItems: "right",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Icon sx={{ fontSize: 16, mr: 0.5 }}>add</Icon>
                Aggiungi lead in blocco
              </ArgonButton>
            </Tooltip>
            {/* Add Column Form - Fix positioning and z-index */}
            {newColumnForm && (
              <ArgonBox
                sx={{
                  backgroundColor: darkMode ? "background.dark" : "#ffffff",
                  p: 2,
                  borderRadius: 2,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  position: "absolute",
                  right: 0,
                  top: "100%",
                  mt: 1,
                  zIndex: 9999, // Increased z-index
                  width: isTablet ? theme.functions.pxToRem(250) : theme.functions.pxToRem(300),
                }}
              >
                <ArgonTypography variant="h6" mb={2}>
                  {t("kanban.newColumn")}
                </ArgonTypography>
                <ArgonInput
                  placeholder={t("kanban.newColumnLabel")}
                  value={newColumnTitle}
                  onChange={handleNewColumnTitleChange}
                  fullWidth
                />
                <ArgonBox display="flex" mt={2}>
                  <ArgonButton
                    variant="gradient"
                    color="primary"
                    size="small"
                    onClick={createNewColumn}
                  >
                    {t("kanban.add")}
                  </ArgonButton>
                  <ArgonBox ml={1}>
                    <ArgonButton
                      variant="gradient"
                      color="light"
                      size="small"
                      onClick={closeNewColumnForm}
                    >
                      {t("kanban.cancel")}
                    </ArgonButton>
                  </ArgonBox>
                </ArgonBox>
              </ArgonBox>
            )}
          </ArgonBox>
        </ArgonBox>

        <ArgonBox
          position="relative"
          my={2}
          sx={({
            palette: { light, background },
            functions: { pxToRem },
            borders: { borderRadius },
          }) => ({
            "& .react-kanban-column": {
              backgroundColor: darkMode ? background.dark : "#e5e9ed",
              width: isTablet ? pxToRem(300) : pxToRem(450),
              margin: `0 ${pxToRem(10)}`,
              padding: pxToRem(20),
              borderRadius: borderRadius.xl,
              marginBottom: "20px",
            },
            "& .react-kanban-card--dragging": {
              transform: "rotate(3deg) scale(1.05)",
              boxShadow: "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
              cursor: "grabbing",
              zIndex: 1000,
            },
            "& .react-kanban-card": {
              cursor: "grab",
            },
          })}
        >
          <ArgonBox display="flex" alignItems="flex-start" width="100%">
            <StyledBoard
              initialBoard={board}
              allowAddCard
              allowAddColumn
              disableCardDrag={toggleDeleteMode}
              disableColumnDrag={toggleDeleteMode}
              onCardDragEnd={handleCardMove}
              onColumnDragEnd={(newBoard) => reorderKanbanBoards(newBoard)}
              renderColumnHeader={renderColumnHeader}
              renderCard={renderCard}
            />
          </ArgonBox>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </div>
  );
}

export default Kanban;
